import {
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from "@tanstack/react-query";
import { useMapSelectOptions } from "hooks/useMapSelectOptions";
import { useAccessToken, useStandardErrorHandler } from "modules/common";
import { toast } from "react-toastify";
import {
    createPhytoViewEntry,
    editPhytoViewEntry,
    getOnePhytoView,
    getPhytoViewEntries,
} from "services/phytoview";
import { GetPhytoViewEntriesRequest } from "services/phytoview/request";
import {
    PhytoviewEntry,
    PhytoViewEntryResponse,
    PhytoviewRequest,
} from "services/phytoview/response";
import { ISelectOption } from "types/selectLabel";
import { PredmetOpazanjaType } from "../constants/PredmetOpazanjaType";

export const useGetPhytoViewEntries = (
    data: GetPhytoViewEntriesRequest,
    endpoint: string,
    options?: UseQueryOptions<PhytoViewEntryResponse>
) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju podataka");
    const accessToken = useAccessToken();
    const { enabled, ...opts } = options ?? {};
    return useQuery({
        queryKey: ["phytoview", endpoint, data],
        queryFn: () => getPhytoViewEntries(data, endpoint, accessToken!),
        onError: errorHandler,
        keepPreviousData: true,
        enabled: Boolean(endpoint),
        ...opts,
    });
};

function useGetPhytoViewEntriesAsItems(
    endpoint: string,
    formatResponse: true,
    type?: PredmetOpazanjaType
): ISelectOption[];

function useGetPhytoViewEntriesAsItems(
    endpoint: string,
    formatResponse: false,
    type?: PredmetOpazanjaType
): PhytoviewEntry[];

function useGetPhytoViewEntriesAsItems(
    endpoint: string,
    formatResponse: boolean,
    type?: PredmetOpazanjaType
): ISelectOption[] | PhytoviewEntry[] {
    const { data: phytoviewData } = useGetPhytoViewEntries(
        {
            page: 1,
            pageSize: 1000,
            isActive: true,
            type,
        },
        endpoint
    );
    const mappedOptions = useMapSelectOptions(phytoviewData?.items || [], "id", "name");

    if (!phytoviewData?.items || phytoviewData.items?.length === 0) {
        return [];
    }

    if (endpoint === "StetniOrganizamAtributVrste" && formatResponse) {
        return phytoviewData?.items.map(item => ({
            label: `${item.name} - ${item.kategorija?.name} `,
            id: item.id,
        }));
    }
    if (endpoint === "Fenofaze" && formatResponse) {
        return phytoviewData?.items.map(item => ({
            label: `${item.name} - ${
                typeof item.predmetOpazanjaId != "number" ? item.predmetOpazanjaId?.name : "N/A"
            }`,
            id: item.id,
        }));
    }

    if (formatResponse) {
        return mappedOptions;
    }

    return phytoviewData.items;
}

export const useGetStetniOrganizamVrsteAsItems = () => {
    const { data: phytoviewData } = useGetPhytoViewEntries(
        {
            page: 1,
            pageSize: 1000,
            isActive: true,
        },
        "StetniOrganizamVrste"
    );
    if (!phytoviewData?.items || phytoviewData.items?.length === 0) {
        return [];
    }
    return phytoviewData?.items.map(item => ({
        label: item.name,
        id: item.id,
        category: item.kategorija?.id,
    }));
};
export const useGetCountyAsItems = () => useGetPhytoViewEntriesAsItems("Zupanije", true);
export const useGetKategorijaAsItems = () =>
    useGetPhytoViewEntriesAsItems("KategorijeStetnici", true);
export const useGetKPodKategorijaAsItems = (type?: PredmetOpazanjaType) =>
    useGetPhytoViewEntriesAsItems("PredmetOpazanja", true, type);
export const useGetPodKategorijaFenofazeAsItems = () =>
    useGetPhytoViewEntriesAsItems("PredmetOpazanjaFenofaze", true);
export const useGetKulturaAsItems = () =>
    useGetPhytoViewEntriesAsItems("PredmetOpazanjaKultureFenofaze", true);
export const useGetAtributiOpazanjaAsItems = () =>
    useGetPhytoViewEntriesAsItems("AtributiOpazanjaVrsteNewModel", true);
export const useGetStetniOrganizmiAsItems = () =>
    useGetPhytoViewEntriesAsItems("StetniOrganizamAtributVrste", true);
export const useGetGlavneFazeAsItems = () => useGetPhytoViewEntriesAsItems("Fenofaze", true);

export const useGetCultures = () =>
    useGetPhytoViewEntriesAsItems("PredmetOpazanjaKultureFenofaze", false);
export const useGetPhenophases = () => useGetPhytoViewEntriesAsItems("Fenofaze", false);

export const useGetOnePhytoView = (
    endpoint: string,
    id: number | null,
    options?: UseQueryOptions<PhytoviewEntry>
) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju podataka");
    const accessToken = useAccessToken();
    const { enabled, ...opts } = options ?? {};

    const queryFn = () => getOnePhytoView(endpoint, id, accessToken!);

    return useQuery({
        queryKey: ["phytoview-details", endpoint, id],
        queryFn,
        onError: errorHandler,
        keepPreviousData: true,
        enabled: Boolean(endpoint) && Boolean(id),
        ...opts,
    });
};
export const UseCreatePhytoViewEntry = (
    endpoint: string,
    options?: UseMutationOptions<PhytoviewEntry, unknown, PhytoviewRequest>
) => {
    const queryClient = useQueryClient();
    const accessToken = useAccessToken();
    return useMutation({
        mutationFn: (data: PhytoviewRequest) => createPhytoViewEntry(endpoint, data, accessToken!),
        onError: error => {
            console.error("Greška pri stvaranju unosa:", error);
            toast.error("Greška pri stvaranju unosa u PhytoView!");
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["phytoview", endpoint]);
            toast.success("Unos u PhytoView je uspješno stvoren!");
        },
        ...options,
    });
};
export const UseEditPhytoViewEntry = (
    endpoint: string,
    id: number | null,
    options?: UseMutationOptions<PhytoviewEntry, unknown, PhytoviewRequest>
) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri uređivanju unosa PhytoView");

    return useMutation({
        mutationFn: (data: PhytoviewRequest) =>
            editPhytoViewEntry(endpoint, id, data, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["phytoview", endpoint]);
            queryClient.invalidateQueries(["phytoview-details", endpoint]);
            toast.success("Uspješno izmjenjen PhytoView");
        },
        ...options,
    });
};
